import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Clean brand glyphs (currentColor so they follow text colour).
const Icons = {
  youtube: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M23 7.5a3 3 0 0 0-2.1-2.1C19 5 12 5 12 5s-7 0-8.9.4A3 3 0 0 0 1 7.5 31 31 0 0 0 .6 12 31 31 0 0 0 1 16.5a3 3 0 0 0 2.1 2.1C5 19 12 19 12 19s7 0 8.9-.4a3 3 0 0 0 2.1-2.1A31 31 0 0 0 23.4 12 31 31 0 0 0 23 7.5zM9.8 15.3V8.7l5.7 3.3-5.7 3.3z" /></svg>),
  instagram: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...p}><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" /></svg>),
  linkedin: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3zM9 9h3.8v1.7h.05c.53-1 1.8-2.05 3.7-2.05 3.96 0 4.7 2.6 4.7 6V21H21v-5.3c0-1.26-.02-2.9-1.76-2.9-1.76 0-2.03 1.37-2.03 2.8V21H13V9z" /></svg>),
  facebook: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12z" /></svg>),
  threads: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M16.3 11.2c-.1 0-.2-.1-.3-.1-.2-3-1.8-4.7-4.5-4.7-1.6 0-3 .7-3.8 2l1.5 1c.6-.9 1.4-1.1 2.3-1.1 1.4 0 2.4.9 2.6 2.5-.7-.1-1.3-.2-2-.2-2.4 0-4 1.3-3.9 3.3.1 1.9 1.7 2.9 3.4 2.9 1.9 0 3.3-1.1 3.7-3 .4.6.6 1.4.6 2.3 0 2.6-1.9 4.4-4.9 4.4-3.3 0-5.2-2.4-5.2-6S8 6.1 11.3 6.1c2.2 0 3.9 1 4.8 2.9l1.6-.7c-1.1-2.4-3.4-3.9-6.4-3.9C7.1 4.4 4.3 7.5 4.3 12s2.8 7.6 7 7.6c3.9 0 6.7-2.6 6.7-6.3 0-1.4-.5-2.7-1.4-3.6l-.3-.5zM11.5 15.3c-.9 0-1.6-.5-1.7-1.2-.05-.9.8-1.5 2.1-1.5.6 0 1.1.1 1.7.2-.2 1.6-1.1 2.5-2.1 2.5z" /></svg>),
  whatsapp: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M17.5 14.4c-.3-.15-1.7-.85-2-.95-.26-.1-.46-.15-.65.15-.2.3-.74.95-.9 1.14-.17.2-.33.22-.62.07a8.2 8.2 0 0 1-2.4-1.48 9 9 0 0 1-1.66-2.07c-.17-.3 0-.46.13-.6.13-.14.3-.33.44-.5.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.65-1.57-.9-2.15-.24-.56-.48-.48-.65-.49h-.56c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.5s1.07 2.9 1.22 3.1c.15.2 2.1 3.2 5.08 4.5.71.3 1.26.49 1.7.63.71.22 1.36.19 1.87.12.57-.09 1.7-.7 1.95-1.37.24-.68.24-1.26.17-1.38-.07-.12-.27-.2-.56-.35zM12 2a10 10 0 0 0-8.6 15.05L2 22l5.07-1.33A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.17-1.14l-.3-.18-3 .79.8-2.93-.2-.3A8.2 8.2 0 1 1 12 20.2z" /></svg>),
};

const BRAND = {
  youtube: { name: "YouTube", bg: "#FF0000", aria: "Open GMOAA on YouTube" },
  instagram: { name: "Instagram", bg: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)", aria: "Open GMOAA on Instagram" },
  linkedin: { name: "LinkedIn", bg: "#0A66C2", aria: "Open GMOAA on LinkedIn" },
  facebook: { name: "Facebook", bg: "#1877F2", aria: "Open GMOAA on Facebook" },
  threads: { name: "Threads", bg: "#000000", aria: "Open GMOAA on Threads" },
  whatsapp: { name: "WhatsApp Chat", bg: "#25D366", aria: "Chat with GMOAA on WhatsApp" },
};

const SocialButton = ({ platform, href, label, reduced }) => {
  const [active, setActive] = useState(false);
  const brand = BRAND[platform];
  const Icon = Icons[platform];
  const on = () => setActive(true);
  const off = () => setActive(false);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={brand.aria}
      data-testid={`social-btn-${platform}`}
      onMouseEnter={on} onMouseLeave={off} onFocus={on} onBlur={off}
      style={{ background: active ? brand.bg : "rgba(255,255,255,0.10)", transition: reduced ? "none" : "all 200ms ease", transform: active && !reduced ? "translateY(-2px)" : "none", boxShadow: active && !reduced ? "0 6px 16px rgba(0,0,0,0.25)" : "none" }}
      className={`inline-flex h-11 min-w-11 items-center justify-center gap-2 rounded-full border border-gmoaa-gold px-3 text-sm font-medium sm:h-10 ${active ? "text-white" : "text-gmoaa-cream"}`}
    >
      <Icon width={18} height={18} />
      <span className="hidden sm:inline">{label}</span>
    </a>
  );
};

export const SocialRow = () => {
  const { t } = useLang();
  const [buttons, setButtons] = useState([]);
  const reduced = prefersReducedMotion();
  useEffect(() => { api.socialSettings().then((d) => setButtons(d.buttons || [])).catch(() => setButtons([])); }, []);
  return (
    <div className="flex flex-wrap items-center justify-center gap-3" data-testid="social-row">
      <span className="text-sm font-medium text-gmoaa-cream/90">{t("followUs")}</span>
      {buttons.map((b) => (
        <SocialButton
          key={b.platform}
          platform={b.platform}
          href={b.href}
          reduced={reduced}
          label={b.platform === "whatsapp" ? t("whatsappChat") : BRAND[b.platform].name}
        />
      ))}
    </div>
  );
};
